import {jwtDecode} from 'jwt-decode';
import {generateUUID} from '@di-shared/utils/uuid';
import {isOneAppInterfaceExist, isWebviewInterfaceExist} from '@cci-shared/features/webviewInterfaceWrapper';
import {getMe, postAuthTokenExchange} from '@di-shared/api/cci/auth';
import {setCciBaseUrl, setCciEuApiRequestHeaderInterceptor, setCciKrApiRequestHeaderInterceptor, setCciNaApiRequestHeaderInterceptor} from '@di-shared/api/cci/httpClient';
import {TokenExchangeParams} from '@di-shared/api/cci/types';
import {setDiApiRequestHeaderInterceptor} from '@di-shared/api/di/axiosInstance';
import {APP_NAME, RETRY_COUNT} from '@di-shared/const';
import {getGoogleTagManagerScript, getOneTrustScript} from '@di-shared/features/analytics/getAnalyticsScript';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {i18nInit} from '@di-shared/i18init';
import {initDatadog, setDatadogGlobalContextProperty, setDatadogUser} from '@di-shared/utils/datadog';
import loadScript from '@di-shared/utils/loadScript';
import {tryWithRetry} from '@di-shared/utils/retry';
import {BrandType, RegionType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils, GET_DEVICE_UUID_FUNCTION_NAME} from '@shared/cci/features/webviewUtils';
import {setCookie} from '@shared/cookieUtil';
import {checkExcludeBrand, checkIsNotProduction} from './utils/whitelist';

const {getState, setState} = environmentStore;

const REGION_CCI_API_BASE = {
  [RegionType.KR]: import.meta.env.VITE_CCI_KR_API_URL!,
  [RegionType.EU]: import.meta.env.VITE_CCI_EU_API_URL!,
  [RegionType.NA]: import.meta.env.VITE_CCI_NA_API_URL!,
};

const BRAND = (import.meta.env.VITE_BRAND as BrandType).toLowerCase();

const SERVICE_TYPE: {[key in RegionType]: TokenExchangeParams['serviceType']} = {
  [RegionType.KR]:
    import.meta.env.VITE_BRAND === BrandType.KIA
      ? 'MY_KIA'
      : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
        ? 'MY_HYUNDAI'
        : 'MY_GENESIS',
  [RegionType.EU]: 'DI',
  // [2026-07-02:Jeyoung Lee] FIXME: 임의 설정
  [RegionType.NA]: 'DI',
};

async function wrappingWindowLikeNativeApp() {
  const wrapping = await import('@cci-shared/features/webviewInterfaceWrapper');
  wrapping.default(window);
}

export async function defaultInit() {
  getState().initEnvironment(APP_NAME, window.navigator.userAgent);

  // mock
  // VITE_MOCK: 전체 region mock, VITE_MOCK_REGIONS: 백엔드 미준비 region만 강제 mock (예: 검증계 NA)
  // NA region + MOCK_REGIONS 설정 시: firebase naVinWhitelist에 등록된 VIN은 mock 제외하고 실 API 호출
  const mockRegions = (import.meta.env.VITE_MOCK_REGIONS ?? '').split(',').map((r: string) => r.trim());
  let isNaMockBypassed = false;
  if (mockRegions.includes(getState().region)) {
    const currentVin = window.location.pathname.split('/').find((s) => s.length > 0) ?? '';
    const {default: firebaseStore} = await import('./features/environment/firebaseStore');
    await firebaseStore.getState().init();
    isNaMockBypassed = firebaseStore.getState().checkNaVinWhitelist(currentVin);
    await import('./api/cci/mock');
  }
  if (import.meta.env.VITE_MOCK === 'true' || (mockRegions.includes(getState().region) && !isNaMockBypassed)) {
    await import('./api/cci/mock');
    await import('./api/di/mock');
  }

  // vconsole
  if (import.meta.env.VITE_PROFILE !== 'prod') {
    const {default: VConsole} = await import('vconsole');
    new VConsole();
  }

  // sciprt 동적 호출
  // OneTrust -> GTM 순서 유지 필수
  // 1. EU Kia만 호출
  // 2. (TODO) EU kia prod 환경 스크립트 추가
  if (getState().region === RegionType.EU && checkIsNotProduction() && checkExcludeBrand(BrandType.GENESIS, BrandType.HYUNDAI)) {
    loadScript(getOneTrustScript(import.meta.env.VITE_ONE_TRUST_ID, import.meta.env.VITE_PROFILE === 'prod'));
  }
  // gtm 동적 호출
  // 1. 검증계나 운영계
  // 2. (TODO) EU Kia 추가
  if (import.meta.env.VITE_PROFILE === 'stg' || import.meta.env.VITE_PROFILE === 'prod') {
    const gtmId = getState().region === RegionType.KR ? import.meta.env.VITE_GTM_KR_ID : import.meta.env.VITE_GTM_EU_ID;
    const newGtmId = import.meta.env.VITE_GTM_ID;
    loadScript(getGoogleTagManagerScript(gtmId));
    loadScript(getGoogleTagManagerScript(newGtmId));
  }
  // datadog 초기화
  if (import.meta.env.VITE_DATADOG_PROFILE === 'stg' || import.meta.env.VITE_DATADOG_PROFILE === 'prod') {
    initDatadog(getState().region);
  }

  // markup data-region 주입
  if (getState().region === RegionType.EU) {
    document.body.setAttribute('data-region', 'EU');
  }

  let cciAccessToken;
  const splitPath = window.location.pathname.split('/');
  const queryString = new URLSearchParams(window.location.search);
  const vin = queryString.get('vin');

  if (!isWebviewInterfaceExist()) {
    if (import.meta.env.VITE_PROFILE === 'prod') {
      // token을 가지고 있는 상태에서 auth 경로로 접근한 경우 sessionStorage 저장 & 홈으로 리다이렉션 처리
      cciAccessToken = queryString.get('cciAccessToken');
      if (splitPath.includes('voc-auth') && cciAccessToken) {
        sessionStorage.setItem('cciAccessToken', cciAccessToken);
        window.location.href = `https://di.${BRAND}.com/${vin}/home`;
        return;
      }
      cciAccessToken = window.sessionStorage.getItem('cciAccessToken');
      // 운영 환경에서 cciToken을 가지고 있지 않다면 리다이렉션 처리
      if (!cciAccessToken) {
        window.location.href = `https://${BRAND}.com`;
        return;
      }
    }
    // prod 가 아니고, 모바일 모드라면 래퍼 on
    await wrappingWindowLikeNativeApp();
  }

  setCciBaseUrl(REGION_CCI_API_BASE[getState().region]);
  await setApiRequestHeaderInterceptor();
  await i18nInit(getState().lang, BRAND, getState().region);
  setCookie('region', getState().region.toLowerCase());
  setCookie('locale', getState().locale.toLowerCase());
  setCookie('brand', import.meta.env.VITE_BRAND.toLowerCase());

  // 2. cci user 정보 (puid, sub, ccid) 획득
  try {
    const {puid, sub} = await tryWithRetry(async () => await getMe(), RETRY_COUNT);
    const ccid = await ccWebviewUtils.getCcid();
    // FIXME : theme 미디어 쿼리 쓰게끔 변경
    // const {matches} = window.matchMedia('(prefers-color-scheme: dark)');
    // setState({puid, sub, ccid, theme: matches ? ThemeType.DARK : ThemeType.LIGHT});
    setState({puid, sub, ccid});
  } catch (e) {
    ccWebviewUtils.closeContent();
  }
}

async function setApiRequestHeaderInterceptor() {
  const region = getState().region;
  if (region === RegionType.KR) {
    setCciKrApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
      const requestId = generateUUID();
      setDatadogGlobalContextProperty('app-request-id', requestId);

      const headers: Record<string, string> = {
        'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
        locale: getState().locale,
        'client-os-code': getState().os,
        'client-version': getState().appVersion ?? '',
        'client-os-version': getState().osVersion,
        'app-request-id': requestId,
      };

      // 1. cci AccessToken 획득
      const cciAccessToken = window.sessionStorage.getItem('cciAccessToken');

      const authInfo = cciAccessToken
        ? {authToken: cciAccessToken}
        : import.meta.env.VITE_PROFILE === 'local'
          ? {
              authToken: import.meta.env.VITE_ACCESS_TOKEN,
            }
          : await ccWebviewUtils.getAuthToken();

      if (authInfo.authToken) {
        headers.Authorization = `Bearer ${authInfo.authToken}`;
      }

      return headers;
    });
  }
  if (region === RegionType.NA) {
    setCciNaApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
      const requestId = generateUUID();
      setDatadogGlobalContextProperty('app-request-id', requestId);

      const headers: Record<string, string> = {
        'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
        locale: getState().locale,
        'client-os-code': getState().os,
        'client-version': getState().appVersion ?? '',
        'client-os-version': getState().osVersion,
        'app-request-id': requestId,
      };

      const cciAccessToken = window.sessionStorage.getItem('cciAccessToken');

      const authInfo = cciAccessToken
        ? {authToken: cciAccessToken}
        : import.meta.env.VITE_PROFILE === 'local'
          ? {authToken: import.meta.env.VITE_ACCESS_TOKEN}
          : await ccWebviewUtils.getAuthToken();

      if (authInfo.authToken) {
        headers.Authorization = `Bearer ${authInfo.authToken}`;
      }

      return headers;
    });
  }
  if (region === RegionType.EU) {
    setCciEuApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
      const requestId = generateUUID();
      setDatadogGlobalContextProperty('app-request-id', requestId);

      const headers: Record<string, string> = {
        'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
        locale: getState().locale,
        'client-os-code': getState().os,
        'client-version': getState().appVersion ?? '',
        'client-os-version': getState().osVersion,
        'app-request-id': requestId,
      };

      // 1. cci AccessToken 획득
      const cciAccessToken = window.sessionStorage.getItem('cciAccessToken');
      const cciIdToken = window.sessionStorage.getItem('cciIdToken');
      const cciNonCcsToken = window.sessionStorage.getItem('cciNonCcsToken');

      const authInfo = cciAccessToken
        ? {authToken: cciAccessToken, idToken: cciIdToken, nonCcsToken: cciNonCcsToken}
        : import.meta.env.VITE_PROFILE === 'local'
          ? {
              authToken: import.meta.env.VITE_ACCESS_TOKEN,
              idToken: import.meta.env.VITE_ID_TOKEN,
              nonCcsToken: import.meta.env.VITE_NON_CCS_TOKEN,
            }
          : await ccWebviewUtils.getAuthToken();

      if (authInfo.authToken) {
        headers.Authorization = `Bearer ${authInfo.authToken}`;
        headers.Authentication = authInfo.idToken;
      }

      if (authInfo.nonCcsToken) {
        headers['non-ccs-token'] = authInfo.nonCcsToken;
      }

      return headers;
    });
  }
  setDiApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
      'Client-Timezone': getState().timezone,
    };

    // 3. cci AccessToken 획득
    const cciAccessToken = window.sessionStorage.getItem('cciAccessToken');

    const authInfo = cciAccessToken
      ? {authToken: cciAccessToken}
      : import.meta.env.VITE_PROFILE === 'local'
        ? {
            authToken: import.meta.env.VITE_ACCESS_TOKEN,
          }
        : await ccWebviewUtils.getAuthToken();

    // 4. di AccessToken 획득
    if (getState().region === RegionType.NA) {
      // NA는 token exchange 없이 CCI 토큰 직접 사용
      if (authInfo.authToken) {
        try {
          if (isOneAppInterfaceExist(GET_DEVICE_UUID_FUNCTION_NAME)) {
            const deviceUUID = await ccWebviewUtils.getDeviceUUID();
            setDatadogUser(deviceUUID);
          } else {
            const {uid} = jwtDecode<{uid: string}>(authInfo.authToken);
            setDatadogUser(uid);
          }
        } catch (error) {
          console.error(error);
        }
        headers['Cci-Access-Token'] = authInfo.authToken;
        headers.Authorization = `Bearer ${authInfo.authToken}`;
      }
    } else {
      try {
        const {accessToken: diAccessToken} = await tryWithRetry(
          async () =>
            await postAuthTokenExchange({
              token: authInfo.authToken as string,
              serviceType: SERVICE_TYPE[getState().region],
            }),
          RETRY_COUNT
        );
        if (authInfo.authToken) {
          try {
            if (isOneAppInterfaceExist(GET_DEVICE_UUID_FUNCTION_NAME)) {
              const deviceUUID = await ccWebviewUtils.getDeviceUUID();
              setDatadogUser(deviceUUID);
            } else {
              const {uid} = jwtDecode<{uid: string}>(authInfo.authToken);
              setDatadogUser(uid);
            }
          } catch (error) {
            console.error(error);
          }
          headers['Cci-Access-Token'] = authInfo.authToken;
        }
        if (diAccessToken) {
          headers.Authorization = `Bearer ${diAccessToken}`;
        }
      } catch (e) {
        ccWebviewUtils.closeContent();
      }
    }

    return headers;
  });
}
